import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import styles from './Articles.module.css';

const Articles = () => {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [error, setError] = useState(null);
  const { t, i18n } = useTranslation("global");

  useEffect(() => {
    fetchArticles();
  }, [currentPage, i18n.language]);

  const fetchArticles = async () => {
    try {
      setLoading(true);
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/articles?page=${currentPage}`,
        {
          headers: {
            "Accept-Language": i18n.language,
          },
        }
      );

      if (!response.ok) {
        throw new Error('Failed to fetch articles');
      }

      const data = await response.json();
      
      if (data.data && Array.isArray(data.data)) {
        setArticles(data.data);
        setTotalPages(data.meta?.last_page || 1);
      } else {
        setArticles([]);
        setTotalPages(1);
      }
      
      setError(null);
    } catch (err) {
      console.error('Error fetching articles:', err);
      setError('Failed to load articles');
      setArticles([]);
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    window.scrollTo(0, 0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString(i18n.language === 'ar' ? 'ar-EG' : 'en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch (err) {
      return dateString;
    }
  };

  const truncateText = (text, maxLength = 200) => {
    if (!text) return '';
    
    // Remove HTML tags for length calculation
    const plainText = text.replace(/<[^>]*>/g, '');
    
    if (plainText.length <= maxLength) return plainText;
    return plainText.substring(0, maxLength) + '...';
  };

  const getArticleTitle = (article) => {
    if (i18n.language === 'ar') {
      return article.title_ar || article.title_en || t("articles.noTitle");
    } else {
      return article.title_en || article.title_ar || t("articles.noTitle");
    }
  };

  const getArticleContent = (article) => {
    if (i18n.language === 'ar') {
      return article.details_ar || article.details_en || '';
    } else {
      return article.details_en || article.details_ar || '';
    }
  };

  if (loading) {
    return (
      <div className={styles.loadingContainer}>
        <div className={styles.spinner}></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.articlesContainer}>
        <div className={styles.articlesHeader}>
          <h1 className={styles.articlesTitle}>{t("articles.title") || "Articles"}</h1>
          <p className={styles.articlesSubtitle}>
            {t("articles.subtitle") || "Discover our latest articles and insights"}
          </p>
        </div>
        <div className="container">
          <div className={styles.noArticles}>
            <div className={styles.noArticlesIcon}>⚠️</div>
            <h3 className={styles.noArticlesTitle}>{t("articles.errorTitle") || "Error Loading Articles"}</h3>
            <p className={styles.noArticlesText}>{error}</p>
            <button 
              className={styles.readMoreBtn}
              onClick={() => fetchArticles()}
            >
              {t("articles.retry") || "Try Again"}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.articlesContainer}>
      <div className={styles.articlesHeader}>
        <h1 className={styles.articlesTitle}>{t("articles.title") || "Articles"}</h1>
        <p className={styles.articlesSubtitle}>
          {t("articles.subtitle") || "Discover our latest articles and insights"}
        </p>
      </div>

      <div className="container">
        {articles.length > 0 ? (
          <>
            <div className={styles.articlesGrid}>
              {articles.map((article, index) => (
                <div key={article.id || index} className={styles.articleCard}>
                  {article.image && (
                    <img
                      src={article.image}
                      alt={getArticleTitle(article)}
                      className={styles.articleImage}
                      onError={(e) => {
                        e.target.src = 'https://via.placeholder.com/400x200?text=Article+Image';
                      }}
                    />
                  )}
                  <div className={styles.articleContent}>
                    <h3 className={styles.articleTitle}>
                      {getArticleTitle(article)}
                    </h3>
                    <p className={styles.articleExcerpt}>
                      {truncateText(getArticleContent(article))}
                    </p>
                    <div className={styles.articleMeta}>
                      <span className={styles.articleDate}>
                        {formatDate(article.created_at)}
                      </span>
                      <span className={styles.articleAuthor}>
                        {article.author || t("articles.unknownAuthor") || "Unknown Author"}
                      </span>
                    </div>
                    <div className={styles.articleActions}>
                      <Link 
                        to={`/articleDetails/${article.id}`}
                        className={styles.readMoreBtn}
                      >
                        {t("articles.readMore") || "اقرأ المزيد"}
                      </Link>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {totalPages > 1 && (
              <div className={styles.pagination}>
                <button
                  className={styles.paginationButton}
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                >
                  {t("articles.previous") || "Previous"}
                </button>
                
                {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                  <button
                    key={page}
                    className={`${styles.paginationButton} ${currentPage === page ? styles.active : ''}`}
                    onClick={() => handlePageChange(page)}
                  >
                    {page}
                  </button>
                ))}
                
                <button
                  className={styles.paginationButton}
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                >
                  {t("articles.next") || "Next"}
                </button>
              </div>
            )}
          </>
        ) : (
          <div className={styles.noArticles}>
            <div className={styles.noArticlesIcon}>📝</div>
            <h3 className={styles.noArticlesTitle}>
              {t("articles.noArticlesTitle") || "No Articles Available"}
            </h3>
            <p className={styles.noArticlesText}>
              {t("articles.noArticlesText") || "Check back later for new articles and insights."}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Articles;
